import { z } from "zod";
import { Id } from "../common/ids.js";
import { Timestamp } from "../common/time.js";
import { defineRoute } from "../http/route.js";
import { ConversationType } from "./conversation.routes.js";
/**
 * Message content for support (ADM-5, card 081; owner, 2026-10-04): only on a reason and step-up, for 30 minutes, and
 * only in conversations between two or more enterprises, whose admins see every access. Under such a grant, an
 * operator may remove a message for a legal reason (card 126; platform terms 8.7).
 */
const Content = { surface: "operator", auth: "operator", permission: "ops:support.content-access" };
export const ContentAccessGrant = z
    .object({
    id: Id.describe("The reference an enterprise's admin quotes"),
    conversationId: Id,
    reason: z.string(),
    grantedAt: Timestamp,
    expiresAt: Timestamp,
})
    .meta({ id: "ContentAccessGrant" });
const TextBody = z.object({ kind: z.literal("text"), text: z.string() });
const FileBody = z.object({
    kind: z.literal("file"),
    name: z.string(),
    contentType: z.string(),
    sizeBytes: z.number().int().nonnegative(),
});
/** What a recalled or removed text or file said, kept in the log (ADR 0029, section 7). */
const Was = z
    .discriminatedUnion("kind", [TextBody, FileBody])
    .optional()
    .describe("What it said, kept in the log: for support under a grant, and in the sender's own export for a text or file they recalled (card 159); no one else reads it");
/** What a message shows a reader outside the desktops: support under a grant, or a party picking evidence. */
export const ContentMessageBody = z
    .discriminatedUnion("kind", [
    TextBody,
    z.object({ kind: z.literal("card"), entityType: z.string(), entityId: z.string() }),
    FileBody,
    z.object({ kind: z.literal("notice"), code: z.string() }),
    z.object({ kind: z.literal("recalled"), was: Was }),
    z
        .object({ kind: z.literal("removed"), was: Was })
        .describe("Civentia removed it for a legal reason (card 126)"),
    z
        .object({ kind: z.literal("not_shown") })
        .describe("Only in evidence copied from OpenIM, before the log (card 159)"),
])
    .meta({ id: "ContentMessageBody", description: "Files show their names only; nothing here downloads one" });
export const ContentMessage = z
    .object({
    id: z.string(),
    sentAt: Timestamp,
    sender: z.object({
        userId: Id.nullable().describe("Null for the platform's own notices"),
        displayName: z.string().describe("Empty when the sender is not a participant"),
        enterpriseId: Id.nullable(),
    }),
    body: ContentMessageBody,
})
    .meta({ id: "ContentMessage" });
/** A message as support reads it from the conversation's log, with its place there. */
export const SupportMessage = ContentMessage.extend({
    id: Id.describe("The entry's id, by which support removes it"),
    seq: z.number().int().positive().describe("Its place in the conversation"),
}).meta({ id: "SupportMessage" });
/** Why Civentia removed a message (card 126; platform terms 8.7). */
export const ContentRemovalCategory = z
    .enum(["authority_direction", "online_harm_notice", "ip_notice", "other"])
    .meta({
    id: "ContentRemovalCategory",
    description: "A direction from an authority, an online harm notice, an IP notice, or another legal reason",
});
export const ContentRemoval = z
    .object({
    id: Id,
    conversationId: Id,
    messageId: Id.nullable().describe("The entry removed; null for a removal made in OpenIM, before the log"),
    seq: z.number().int().positive(),
    category: ContentRemovalCategory,
    reason: z.string().describe("The operator's reason, as recorded"),
    reasonWithheld: z
        .boolean()
        .describe("The law forbids saying why: the enterprises read that it was removed to comply with the law"),
    file: z
        .object({ id: Id, name: z.string() })
        .nullable()
        .describe("The file the message carried, removed with it"),
    removedAt: Timestamp,
})
    .meta({ id: "ContentRemoval" });
export const grantContentAccess = defineRoute({
    ...Content,
    id: "ops.conversations.content-access.grant",
    method: "POST",
    path: "/v1/ops/conversations/{conversationId}/content-access",
    marks: ["step-up"],
    summary: "Open a conversation's messages to this operator for 30 minutes, with a reason the enterprises' admins see; only a conversation between two or more enterprises",
    prd: ["ADM-5"],
    request: {
        params: z.object({ conversationId: Id }),
        body: z.object({ reason: z.string().trim().min(10).max(500) }),
    },
    responses: { 201: { description: "The grant", schema: ContentAccessGrant } },
    errors: [
        "AUTH_REQUIRED",
        "PERMISSION_DENIED",
        "STEP_UP_REQUIRED",
        "VALIDATION_FAILED",
        "NOT_FOUND",
        "NOT_READABLE",
    ],
});
export const listContentMessages = defineRoute({
    ...Content,
    id: "ops.conversations.messages.list",
    method: "GET",
    path: "/v1/ops/conversations/{conversationId}/messages",
    marks: [],
    summary: "A page of the conversation's log, newest first, while this operator holds a grant for it: what a recalled or removed text or file said, marked so",
    prd: ["ADM-5"],
    request: {
        params: z.object({ conversationId: Id }),
        query: z.object({ page: z.coerce.number().int().min(1).max(1000).default(1) }),
    },
    responses: {
        200: {
            description: "Messages",
            schema: z.object({
                expiresAt: Timestamp.describe("When this operator's grant ends"),
                items: z.array(SupportMessage),
                nextPage: z.number().int().positive().nullable(),
            }),
        },
    },
    errors: ["AUTH_REQUIRED", "PERMISSION_DENIED", "NOT_FOUND", "CONTENT_ACCESS_REQUIRED"],
});
export const removeContentMessage = defineRoute({
    ...Content,
    permission: "ops:content.remove",
    id: "ops.conversations.messages.remove",
    method: "POST",
    path: "/v1/ops/conversations/{conversationId}/messages/{messageId}/remove",
    marks: ["step-up"],
    summary: "Remove a person's text or file for a legal reason, while this operator holds a grant for the conversation: every member sees it removed by Civentia, and the sender's enterprise's admins are told why",
    prd: ["ADM-5"],
    request: {
        params: z.object({ conversationId: Id, messageId: Id }),
        body: z.object({
            category: ContentRemovalCategory,
            reason: z.string().trim().min(10).max(2000),
            withholdReason: z
                .boolean()
                .default(false)
                .describe("The law forbids saying why: the enterprises read that it was removed to comply with the law"),
        }),
    },
    responses: { 201: { description: "The removal", schema: ContentRemoval } },
    errors: [
        "AUTH_REQUIRED",
        "PERMISSION_DENIED",
        "STEP_UP_REQUIRED",
        "VALIDATION_FAILED",
        "NOT_FOUND",
        "CONTENT_ACCESS_REQUIRED",
        "INVALID_TRANSITION",
    ],
});
/** A message Civentia removed under a grant, as the enterprises' admins read it (card 126). */
export const ContentRemovalEntry = z
    .object({
    reference: Id.describe("The removal's reference, to quote"),
    removedAt: Timestamp,
    fileName: z.string().nullable().describe("The removed file's name, when the message carried one"),
    reason: z.string().nullable().describe("Null when the law forbids saying why"),
    reasonWithheld: z.boolean(),
})
    .meta({ id: "ContentRemovalEntry" });
/** One access to the enterprise's conversations, as its admins see it: never the operator's name. */
export const ContentAccessEntry = z
    .object({
    reference: Id,
    by: z.literal("platform_support"),
    reason: z.string(),
    grantedAt: Timestamp,
    expiresAt: Timestamp,
    conversation: z.object({ id: Id, type: ConversationType, name: z.string().nullable() }),
    removals: z.array(ContentRemovalEntry).describe("What Civentia removed under this access, oldest first"),
})
    .meta({ id: "ContentAccessEntry" });
export const listEnterpriseContentAccess = defineRoute({
    surface: "tenant",
    auth: "user",
    permission: "enterprise:manage",
    id: "enterprise.content-access.list",
    method: "GET",
    path: "/v1/enterprise/content-access",
    marks: ["tenant"],
    summary: "Every time platform support read one of the enterprise's conversations, newest first, with its reason and what it removed",
    prd: ["ADM-5"],
    responses: { 200: { description: "Accesses", schema: z.object({ items: z.array(ContentAccessEntry) }) } },
    errors: ["AUTH_REQUIRED", "TENANT_REQUIRED", "PERMISSION_DENIED"],
});
export const contentAccessRoutes = [
    grantContentAccess,
    listContentMessages,
    removeContentMessage,
    listEnterpriseContentAccess,
];
//# sourceMappingURL=content-access.routes.js.map